import React, { useEffect, useRef, useState } from "react";
import { ClientEvent, MatrixEventEvent } from "matrix-js-sdk";
import {
    MeetingJoinRequestActions,
    MeetingJoinRequestButton,
    MeetingJoinRequestCard,
    MeetingJoinRequestStack,
    MeetingJoinRequestText,
    Toast
} from "src/components";
import { getMeeting, updateMeetingAdmission } from "src/api/meetingsApi";
import CloseIcon from "src/img/new/chats/close.svg";
import { UserKeys } from "src/constants/userDetails";
import { IsFeatureReady } from "src/constants/features";
import { MEETING_PARTICIPANT_NOTIFICATION } from "src/constants/matrixEventTypes";
import BackgroundEffectsPicker from "src/scenes/Chat/components/call/BackgroundEffectsPicker";
import CallLobby from "src/scenes/Chat/components/call/CallLobby";
import {
    getCallParticipantLabel,
    getInitials,
    getSelectedCustomBackgroundUrl,
    normalizeBackgroundEffect
} from "src/scenes/Chat/components/call/ChatCallPanel.constants";
import LiveCallControls from "src/scenes/Chat/components/call/LiveCallControls";
import LiveCallHeader from "src/scenes/Chat/components/call/LiveCallHeader";
import LiveSettingsPanel from "src/scenes/Chat/components/call/LiveSettingsPanel";
import StreamVideo from "src/scenes/Chat/components/call/StreamVideo";
import MeetingManagementPanel from "src/scenes/Chat/components/call/MeetingManagementPanel";
import { getLocalMediaState } from "src/scenes/Chat/utils/participantMediaState";
import CallNotesPanel from "./CallNotesPanel";

import {
    ActionButton,
    Actions,
    CallStage,
    CloseButtonIcon,
    CollapsedNotesButton,
    Detail,
    Details,
    Eyebrow,
    FloatingWindow,
    HeaderActions,
    HeaderButton,
    HeaderInfo,
    IncomingPopupAvatar,
    IncomingPopupAvatarStack,
    IncomingPopupIdentity,
    IncomingPopupName,
    IncomingPopupOverflow,
    INITIAL_POSITION,
    LiveCallContent,
    LiveCallMain,
    LiveCallOverlayPanel,
    LiveCallShell,
    MediaGrid,
    MINIMIZED_WIDTH,
    MinimizedBody,
    ScreenShareRail,
    Title,
    WINDOW_HEIGHT,
    WINDOW_WIDTH,
    WindowBody,
    WindowHeader
} from "./ChatCallPanel.styled";

const getBoundedPosition = (position, isMinimized = false) => {
    const width = isMinimized ? MINIMIZED_WIDTH : Math.min(WINDOW_WIDTH, window.innerWidth - 32);
    const height = isMinimized ? 112 : Math.min(WINDOW_HEIGHT, window.innerHeight - 32);
    const maxX = Math.max(16, window.innerWidth - width - 16);
    const maxY = Math.max(16, window.innerHeight - height - 16);

    return {
        x: Math.min(Math.max(16, position.x), maxX),
        y: Math.min(Math.max(16, position.y), maxY)
    };
};

const ChatCallPanel = ({
    callState,
    callNotes = [],
    isSendingCallNote = false,
    onAddCallNote,
    onDeleteCallNote,
    onEditCallNote,
    currentChat,
    currentUser,
    matrixClient,
    currentMatrixUserId: loggedInMatrixUserId,
    callLobby,
    isSendingCallAction,
    screenShareIsActive,
    localStream,
    localScreenShareStream,
    remoteStreams = [],
    onCancelLobby,
    onStartFromLobby,
    onFocusWindow,
    onSetLocalAudioEnabled,
    onSetLocalMicrophoneDevice,
    onSetLocalVideoEnabled,
    onSetLocalCameraDevice,
    onSetLocalSpeakerDevice,
    onToggleScreenShare,
    onSetLocalBackgroundEffect,
    onAnswerFromLobby,
    onHangup,
    onReject,
    dedicatedRuntime = false
}) => {
    const [isMinimized, setIsMinimized] = useState(false);
    const [position, setPosition] = useState(INITIAL_POSITION);
    const [liveBackgroundPanelIsOpen, setLiveBackgroundPanelIsOpen] = useState(false);
    const [settingsPanelIsOpen, setSettingsPanelIsOpen] = useState(false);
    const [meetingPanelIsOpen, setMeetingPanelIsOpen] = useState(false);
    const [notesPanelIsCollapsed, setNotesPanelIsCollapsed] = useState(false);
    const [liveBackgroundEffectIsApplying, setLiveBackgroundEffectIsApplying] = useState(false);
    const [liveBackgroundEffectError, setLiveBackgroundEffectError] = useState("");
    const [waitingGuests, setWaitingGuests] = useState([]);
    const [pendingAdmissionIds, setPendingAdmissionIds] = useState(new Set());
    const [liveBackgroundEffect, setLiveBackgroundEffect] = useState(normalizeBackgroundEffect(localStorage.getItem("meBusinessCallBackground") || "none"));
    const [customBackgroundUrl, setCustomBackgroundUrl] = useState(getSelectedCustomBackgroundUrl);
    const [microphoneDeviceId, setMicrophoneDeviceId] = useState(localStorage.getItem("meBusinessCallMicrophone") || "");
    const [cameraDeviceId, setCameraDeviceId] = useState(localStorage.getItem("meBusinessCallCamera") || "");
    const [speakerDeviceId, setSpeakerDeviceId] = useState(localStorage.getItem("meBusinessCallSpeaker") || "");
    const [now, setNow] = useState(Date.now());
    const dragStateRef = useRef(null);
    const overlayPanelRef = useRef(null);
    const shownGuestJoinEventIdsRef = useRef(new Set());
    const hasLobby = callLobby?.isOpen === true;
    const hasAnswerLobby = callState?.status === "inbound";
    const shouldUseDedicatedWindow = dedicatedRuntime;
    const shouldShowInPageFallback = !shouldUseDedicatedWindow && callLobby?.popupBlocked === true;
    const hasMeetingControls = Boolean(
        IsFeatureReady.publicMeetings() &&
        currentChat?.meetingUuid &&
        currentChat?.meetingCanManage === true
    );
    const localMediaState = getLocalMediaState(localStream);
    const localAudioIsEnabled = localMediaState.microphoneEnabled;
    const localVideoIsEnabled = localMediaState.cameraEnabled;

    useEffect(() => {
        if (!callState || callState.status === "idle") {
            setIsMinimized(false);
            setLiveBackgroundPanelIsOpen(false);
            setSettingsPanelIsOpen(false);
            setNotesPanelIsCollapsed(false);
            setPosition(INITIAL_POSITION);
        }
    }, [callState?.callId, callState?.groupCallId, callState?.status]);

    useEffect(() => {
        if (callState?.status !== "on_call" || !callState?.startedAt) {
            setNow(Date.now());
            return undefined;
        }

        const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(intervalId);
    }, [callState?.startedAt, callState?.status]);

    useEffect(() => {
        if (callState?.status !== "on_call") {
            return;
        }

        setLiveBackgroundEffect(normalizeBackgroundEffect(localStorage.getItem("meBusinessCallBackground") || "none"));
        setCustomBackgroundUrl(getSelectedCustomBackgroundUrl());
    }, [callState?.status, callState?.callId, callState?.groupCallId]);

    useEffect(() => {
        if (!liveBackgroundPanelIsOpen && !settingsPanelIsOpen && !meetingPanelIsOpen) {
            return undefined;
        }

        const dismissOverlayPanel = event => {
            const target = event.target;
            if (overlayPanelRef.current?.contains(target) || target?.closest?.("[data-call-overlay-toggle='true']")) {
                return;
            }

            setLiveBackgroundPanelIsOpen(false);
            setMeetingPanelIsOpen(false);
            setSettingsPanelIsOpen(false);
        };

        document.addEventListener("pointerdown", dismissOverlayPanel);
        return () => document.removeEventListener("pointerdown", dismissOverlayPanel);
    }, [liveBackgroundPanelIsOpen, meetingPanelIsOpen, settingsPanelIsOpen]);

    useEffect(() => {
        if (!hasMeetingControls || !["outbound", "on_call"].includes(callState?.status)) {
            setWaitingGuests([]);
            return undefined;
        }

        let disposed = false;
        const refreshWaitingGuests = async () => {
            try {
                const meeting = await getMeeting(currentChat.meetingUuid);
                if (disposed) {
                    return;
                }
                const waitingGuests = meeting.participants.filter(participant => (
                    participant.admissionStatus === "waiting"
                ));
                setWaitingGuests(waitingGuests);
            } catch {
                // Keep the active call usable if a background management refresh fails.
            }
        };

        refreshWaitingGuests();
        const intervalId = window.setInterval(refreshWaitingGuests, 3000);
        return () => {
            disposed = true;
            window.clearInterval(intervalId);
        };
    }, [callState?.status, currentChat?.meetingUuid, hasMeetingControls]);

    useEffect(() => {
        if (!IsFeatureReady.publicMeetings() || !matrixClient || !currentChat?.matrixRoomId) {
            return undefined;
        }
        const showGuestJoinedToast = (event, error) => {
            if (
                error
                || event?.getType?.() !== MEETING_PARTICIPANT_NOTIFICATION
                || event?.getRoomId?.() !== currentChat.matrixRoomId
                || event?.getSender?.() === matrixClient.getUserId?.()
            ) {
                return;
            }
            const eventId = event.getId?.() || "";
            if (eventId && shownGuestJoinEventIdsRef.current.has(eventId)) {
                return;
            }
            const content = event.getContent?.() || {};
            if (content.action !== "joined" || content.participant_type !== "guest" || !content.display_name) {
                return;
            }
            if (content.created_at && Date.now() - content.created_at > 30000) {
                return;
            }
            if (eventId) {
                shownGuestJoinEventIdsRef.current.add(eventId);
            }
            Toast.success(`${content.display_name} (Guest) joined the meeting.`);
        };

        matrixClient.on(ClientEvent.Event, showGuestJoinedToast);
        matrixClient.on(MatrixEventEvent.Decrypted, showGuestJoinedToast);
        matrixClient.getRoom(currentChat.matrixRoomId)
            ?.getUnfilteredTimelineSet()
            .getLiveTimeline()
            .getEvents()
            .forEach(event => showGuestJoinedToast(event));
        return () => {
            matrixClient.off(ClientEvent.Event, showGuestJoinedToast);
            matrixClient.off(MatrixEventEvent.Decrypted, showGuestJoinedToast);
        };
    }, [currentChat?.matrixRoomId, matrixClient]);

    const respondToJoinRequest = async (participantUuid, status) => {
        if (pendingAdmissionIds.has(participantUuid)) {
            return;
        }
        setPendingAdmissionIds(previous => new Set(previous).add(participantUuid));
        try {
            const participant = await updateMeetingAdmission(currentChat.meetingUuid, participantUuid, status);
            setWaitingGuests(previous => previous.filter(guest => guest.participantUuid !== participantUuid));
            if (status === "admitted") {
                Toast.success(`${participant.displayName} (Guest) joined the meeting.`);
                if (matrixClient && currentChat.matrixRoomId) {
                    matrixClient.sendEvent(currentChat.matrixRoomId, MEETING_PARTICIPANT_NOTIFICATION, {
                        action: "joined",
                        created_at: Date.now(),
                        display_name: participant.displayName,
                        matrix_user_id: participant.matrixUserId,
                        participant_type: "guest",
                        participant_uuid: participant.participantUuid
                    }).catch(error => {
                        console.warn("Unable to notify the other meeting participants that the guest joined.", error);
                    });
                }
            } else {
                Toast.success("Guest declined.");
            }
        } catch (error) {
            Toast.error(error.getApiErrorDetails?.().message || error.message || "Unable to update the join request.");
        } finally {
            setPendingAdmissionIds(previous => {
                const nextIds = new Set(previous);
                nextIds.delete(participantUuid);
                return nextIds;
            });
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setPosition(previous => getBoundedPosition(previous, isMinimized));
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isMinimized]);

    useEffect(() => {
        const handlePointerMove = event => {
            if (!dragStateRef.current) {
                return;
            }

            const nextPosition = {
                x: event.clientX - dragStateRef.current.offsetX,
                y: event.clientY - dragStateRef.current.offsetY
            };
            setPosition(getBoundedPosition(nextPosition, isMinimized));
        };

        const handlePointerUp = () => {
            dragStateRef.current = null;
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
        };
    }, [isMinimized]);

    if ((!callState || callState.status === "idle") && !hasLobby && !shouldShowInPageFallback) {
        return null;
    }

    if (!shouldUseDedicatedWindow && !shouldShowInPageFallback) {
        return null;
    }

    const activeDisplayChat = callLobby?.chat || currentChat;
    const currentMatrixUserId = activeDisplayChat?.matrixClient?.getUserId?.() ||
        currentChat?.matrixClient?.getUserId?.() ||
        loggedInMatrixUserId ||
        currentUser?.[UserKeys.MATRIX_USER_ID] ||
        "";
    const getChatUser = (matrixUserId) => {
        return [
            ...(activeDisplayChat?.users || []),
            ...(currentChat?.users || [])
        ].find(chatUser => chatUser.matrixUserId === matrixUserId);
    };

    const getUserFullName = (user) => {
        return `${user?.employeeDetails?.firstName || user?.firstName || ""} ${user?.employeeDetails?.lastName || user?.lastName || ""}`.trim();
    };

    const getUserDisplayName = (matrixUserId, fallbackLabel) => {
        const user = getChatUser(matrixUserId);
        let displayName = "";

        if (!user) {
            displayName = activeDisplayChat?.matrixRoom?.getMember?.(matrixUserId)?.name ||
                currentChat?.matrixRoom?.getMember?.(matrixUserId)?.name ||
                fallbackLabel ||
                matrixUserId;
        } else {
            displayName = getUserFullName(user) ||
                activeDisplayChat?.matrixRoom?.getMember?.(matrixUserId)?.name ||
                currentChat?.matrixRoom?.getMember?.(matrixUserId)?.name ||
                matrixUserId ||
                fallbackLabel;
        }

        return getCallParticipantLabel(matrixUserId, displayName);
    };

    const displayedCallNotes = callNotes.map(note => {
        const isMine = note.authorUserId === currentMatrixUserId;

        return {
            ...note,
            isMine,
            authorName: isMine
                ? "You"
                : note.authorName || getUserDisplayName(note.authorUserId, "Participant")
        };
    });

    const getUserAvatarUrl = (matrixUserId) => {
        if (matrixUserId && matrixUserId === currentMatrixUserId) {
            return currentUser?.[UserKeys.PROFILE_PICTURE_URL] ||
                getChatUser(matrixUserId)?.profilePictureUrl ||
                getChatUser(matrixUserId)?.employeeDetails?.profilePictureUrl ||
                "";
        }

        const user = getChatUser(matrixUserId);
        return user?.profilePictureUrl || user?.employeeDetails?.profilePictureUrl || "";
    };

    const getUserInitials = (matrixUserId, fallbackDisplayName) => {
        if (matrixUserId && matrixUserId === currentMatrixUserId) {
            const currentUserFullName = `${currentUser?.[UserKeys.FIRST_NAME] || ""} ${currentUser?.[UserKeys.LAST_NAME] || ""}`.trim();
            return getInitials(currentUserFullName || getUserDisplayName(matrixUserId, "You"));
        }

        const user = getChatUser(matrixUserId);
        return getInitials(user
            ? getUserFullName(user)
            : getUserDisplayName(matrixUserId, fallbackDisplayName));
    };

    const callerName = getUserDisplayName(callState.sender, "the caller");
    const visibleChat = hasLobby ? callLobby.chat : currentChat;
    const callDetail = hasLobby
        ? (visibleChat?.name || visibleChat?.groupName || "Me Business call")
        : callState.status === "inbound" && callState.sender
            ? `Call from ${callerName}`
            : callState.isGroupCall
                ? `Call in ${currentChat?.name || currentChat?.groupName || "group chat"}`
                : "Call";
    const statusText = hasLobby ? "Pre-call setup" : {
        inbound: "Incoming call",
        outbound: "Calling",
        on_call: "On call"
    }[callState.status] || "Call";
    const remoteScreenShare = remoteStreams.find(item => item.isScreenShare);
    const remoteParticipantStreams = remoteStreams.filter(item => !item.isScreenShare);
    const activeScreenShare = screenShareIsActive
        ? { stream: localScreenShareStream, videoTrack: null, peerUserId: currentMatrixUserId }
        : remoteScreenShare;
    const callScreenShareIsActive = Boolean(activeScreenShare?.stream);
    const mediaCount = remoteParticipantStreams.length + 1;
    const participantCount = Math.max(1, remoteParticipantStreams.length + 1);
    const isOneToOneCall = callState.status === "on_call" &&
        !callState.isGroupCall &&
        remoteParticipantStreams.length === 1 &&
        !callScreenShareIsActive;
    const activeSidePanel = liveBackgroundPanelIsOpen
        ? "backgrounds"
        : settingsPanelIsOpen ? "settings" : meetingPanelIsOpen ? "meeting" : "";

    const startDrag = event => {
        if (event.button !== 0 || event.target.closest("button")) {
            return;
        }

        dragStateRef.current = {
            offsetX: event.clientX - position.x,
            offsetY: event.clientY - position.y
        };
    };

    const handleClose = () => {
        if (callState.status === "inbound") {
            onReject();
            return;
        }

        onHangup();
    };

    const startAnswerFromLobby = async (settings) => {
        onFocusWindow?.();
        await onAnswerFromLobby?.(settings);
    };

    const toggleLocalAudio = () => {
        const nextEnabled = !localAudioIsEnabled;
        onSetLocalAudioEnabled?.(nextEnabled);
    };

    const toggleLocalVideo = async () => {
        const nextEnabled = !localVideoIsEnabled;
        await onSetLocalVideoEnabled?.(nextEnabled);
    };

    const updateLiveBackgroundEffect = async (effect) => {
        const nextEffect = normalizeBackgroundEffect(effect);
        const previousEffect = liveBackgroundEffect;
        setLiveBackgroundEffect(nextEffect);
        setLiveBackgroundEffectError("");
        setLiveBackgroundEffectIsApplying(true);
        try {
            const result = await onSetLocalBackgroundEffect?.(nextEffect);
            if (result === false) {
                setLiveBackgroundEffect(previousEffect);
                localStorage.setItem("meBusinessCallBackground", previousEffect);
                setLiveBackgroundEffectError("The background effect could not be applied. Please try again.");
            }
        } catch (error) {
            console.warn("Unable to apply the background effect.", error);
            setLiveBackgroundEffect(previousEffect);
            localStorage.setItem("meBusinessCallBackground", previousEffect);
            setLiveBackgroundEffectError("The background effect could not be applied. Please try again.");
            Toast.error("The background effect could not be applied. Please try again.");
        } finally {
            setLiveBackgroundEffectIsApplying(false);
        }
    };

    const changeMicrophoneDevice = async (deviceId) => {
        setMicrophoneDeviceId(deviceId);
        const result = await onSetLocalMicrophoneDevice?.(deviceId);
        if (result === false) {
            setMicrophoneDeviceId(localStorage.getItem("meBusinessCallMicrophone") || "");
        }
    };

    const changeCameraDevice = async (deviceId) => {
        setCameraDeviceId(deviceId);
        const result = await onSetLocalCameraDevice?.(deviceId);
        if (result === false) {
            setCameraDeviceId(localStorage.getItem("meBusinessCallCamera") || "");
        }
    };

    const changeSpeakerDevice = async (deviceId) => {
        setSpeakerDeviceId(deviceId);
        const result = await onSetLocalSpeakerDevice?.(deviceId);
        if (result === false) {
            setSpeakerDeviceId(localStorage.getItem("meBusinessCallSpeaker") || "");
        }
    };

    const remoteParticipantNames = remoteParticipantStreams.map((remoteStreamItem, index) =>
        getUserDisplayName(
            remoteStreamItem.peerUserId,
            remoteStreamItem.displayName || `Participant ${index + 1}`
        )
    );
    const conversationName = visibleChat?.name || visibleChat?.groupName || callDetail || "Me Business call";
    const callerAvatarUrl = getUserAvatarUrl(callState.sender);
    const incomingIdentityUsers = (() => {
        const chatUsers = (visibleChat?.users || []).filter(user => user.matrixUserId !== currentMatrixUserId);
        const caller = chatUsers.find(user => user.matrixUserId === callState.sender);
        const orderedUsers = caller
            ? [caller, ...chatUsers.filter(user => user.matrixUserId !== callState.sender)]
            : chatUsers;

        if (orderedUsers.length > 0) {
            return orderedUsers;
        }

        return callState.sender ? [{ matrixUserId: callState.sender }] : [];
    })();
    const otherChatUser = visibleChat?.users?.find(user => user.matrixUserId !== currentMatrixUserId);
    const conversationAvatarUrl = visibleChat?.isGroupChat
        ? visibleChat?.profilePictureUrl || visibleChat?.avatarUrl || ""
        : getUserAvatarUrl(otherChatUser?.matrixUserId);
    const conversationAvatarInitials = visibleChat?.isGroupChat
        ? ""
        : getUserInitials(otherChatUser?.matrixUserId, conversationName);

    const callWindow = (
        <FloatingWindow
            role="dialog"
            aria-label={statusText}
            $x={position.x}
            $y={position.y}
            $isMinimized={isMinimized}
            $dedicated={shouldUseDedicatedWindow}
            $centered={shouldShowInPageFallback}
        >
            <WindowHeader onPointerDown={shouldUseDedicatedWindow ? undefined : startDrag}>
                <HeaderInfo>
                    <Eyebrow>{statusText}</Eyebrow>
                    <Title>{callDetail}</Title>
                </HeaderInfo>
                <HeaderActions>
                    {!shouldUseDedicatedWindow &&
                        <>
                            <HeaderButton
                                type="button"
                                title={isMinimized ? "Restore call window" : "Minimize call window"}
                                onClick={() => setIsMinimized(previous => !previous)}
                            >
                                {isMinimized ? "+" : "-"}
                            </HeaderButton>
                            <HeaderButton
                                type="button"
                                title={hasLobby ? "Cancel call setup" : callState.status === "inbound" ? "Decline call" : "Leave call"}
                                disabled={isSendingCallAction}
                                onClick={hasLobby ? onCancelLobby : handleClose}
                            >
                                <CloseButtonIcon src={CloseIcon} alt="" />
                            </HeaderButton>
                        </>
                    }
                </HeaderActions>
            </WindowHeader>
            {shouldShowInPageFallback && !hasAnswerLobby ? (
                <WindowBody>
                    <Details>
                        <Detail>Allow pop-ups for Me Business to open the call window.</Detail>
                        <Actions>
                            <ActionButton type="button" disabled={isSendingCallAction} onClick={() => onFocusWindow?.()}>
                                Open call window
                            </ActionButton>
                            <ActionButton type="button" $variant="danger" disabled={isSendingCallAction} onClick={onCancelLobby}>
                                Cancel
                            </ActionButton>
                        </Actions>
                    </Details>
                </WindowBody>
            ) : hasAnswerLobby && !shouldUseDedicatedWindow ? (
                <WindowBody>
                    <Details>
                        <IncomingPopupIdentity>
                            <IncomingPopupAvatarStack aria-label={`${callerName} call participants`}>
                                {incomingIdentityUsers.slice(0, 3).map((user, index) => {
                                    const matrixUserId = user.matrixUserId || `incoming-${index}`;
                                    const avatarUrl = getUserAvatarUrl(user.matrixUserId);
                                    const initials = getUserInitials(user.matrixUserId, callerName);

                                    return (
                                        <IncomingPopupAvatar key={matrixUserId} $src={avatarUrl}>
                                            {avatarUrl ? "" : initials}
                                        </IncomingPopupAvatar>
                                    );
                                })}
                                {incomingIdentityUsers.length > 3 && (
                                    <IncomingPopupOverflow>+{incomingIdentityUsers.length - 3}</IncomingPopupOverflow>
                                )}
                            </IncomingPopupAvatarStack>
                            <IncomingPopupName>{visibleChat?.isGroupChat ? conversationName : callerName}</IncomingPopupName>
                        </IncomingPopupIdentity>
                        <Detail>
                            {callerName} is calling. Allow pop-ups for Me Business to answer in the call window.
                        </Detail>
                        <Actions>
                            <ActionButton type="button" disabled={isSendingCallAction} onClick={() => onFocusWindow?.()}>
                                Join Call
                            </ActionButton>
                            <ActionButton type="button" $variant="danger" disabled={isSendingCallAction} onClick={onReject}>
                                Decline
                            </ActionButton>
                        </Actions>
                    </Details>
                </WindowBody>
            ) : hasLobby || hasAnswerLobby ? (
                <CallLobby
                    actionLabel={hasAnswerLobby ? "Join Call" : "Start Call"}
                    callerAvatarUrl={callerAvatarUrl}
                    callerName={callerName}
                    chat={hasAnswerLobby ? currentChat : callLobby.chat}
                    currentChat={currentChat}
                    getUserDisplayName={getUserDisplayName}
                    isSendingCallAction={isSendingCallAction}
                    onCancel={hasAnswerLobby ? onReject : onCancelLobby}
                    onStart={hasAnswerLobby ? startAnswerFromLobby : onStartFromLobby}
                    showIncomingSummary={hasAnswerLobby}
                />
            ) : isMinimized ? (
                <MinimizedBody type="button" onClick={() => setIsMinimized(false)}>
                    <span>{callState.status === "on_call" ? "Return to call" : callDetail}</span>
                    <span>{callState.isGroupCall ? "Group" : "Call"}</span>
                </MinimizedBody>
            ) : (
                <WindowBody $dedicated={shouldUseDedicatedWindow}>
                    <LiveCallShell $dedicated={shouldUseDedicatedWindow}>
                        <LiveCallHeader
                            avatarInitials={conversationAvatarInitials}
                            avatarUrl={conversationAvatarUrl}
                            callState={callState}
                            conversationName={conversationName}
                            isGroupCall={Boolean(visibleChat?.isGroupChat || callState.isGroupCall)}
                            now={now}
                            participantCount={participantCount}
                        />
                        <LiveCallContent $notesCollapsed={notesPanelIsCollapsed}>
                            <LiveCallMain>
                                <CallStage $compact={callState.status !== "on_call"}>
                                    <MediaGrid $count={mediaCount} $oneToOne={isOneToOneCall} $screenShare={callScreenShareIsActive}>
                                        {callScreenShareIsActive ? (
                                            <>
                                                <StreamVideo
                                                    avatarUrl={getUserAvatarUrl(activeScreenShare.peerUserId)}
                                                    fallbackInitials={getUserInitials(
                                                        activeScreenShare.peerUserId,
                                                        activeScreenShare.displayName
                                                    )}
                                                    label={screenShareIsActive
                                                        ? "You are sharing"
                                                        : `${getUserDisplayName(
                                                            activeScreenShare.peerUserId,
                                                            activeScreenShare.displayName || "Participant"
                                                        )} is sharing`}
                                                    muted
                                                    screenPrimary
                                                    screenSharing
                                                    sinkId={speakerDeviceId}
                                                    stream={activeScreenShare.stream}
                                                    videoTrack={activeScreenShare.videoTrack}
                                                />
                                                <ScreenShareRail>
                                                    <StreamVideo
                                                        audioMuted={!localAudioIsEnabled}
                                                        avatarUrl={getUserAvatarUrl(currentMatrixUserId)}
                                                        fallbackInitials={getUserInitials(currentMatrixUserId)}
                                                        forceCameraOff={!localVideoIsEnabled}
                                                        label="You"
                                                        mirrored
                                                        muted
                                                        sinkId={speakerDeviceId}
                                                        stream={localStream}
                                                        videoEffect={liveBackgroundEffect}
                                                    />
                                                    {remoteParticipantStreams.map((remoteStreamItem, index) => (
                                                        <StreamVideo
                                                            key={remoteStreamItem.callId || remoteStreamItem.peerUserId || index}
                                                            activeSpeaker={remoteStreamItem.isSpeaking}
                                                            avatarUrl={getUserAvatarUrl(remoteStreamItem.peerUserId)}
                                                            fallbackInitials={getUserInitials(
                                                                remoteStreamItem.peerUserId,
                                                                remoteStreamItem.displayName
                                                            )}
                                                            label={remoteParticipantNames[index]}
                                                            sinkId={speakerDeviceId}
                                                            stream={remoteStreamItem.stream}
                                                            videoTrack={remoteStreamItem.videoTrack}
                                                        />
                                                    ))}
                                                </ScreenShareRail>
                                            </>
                                        ) : isOneToOneCall ? (
                                            <>
                                                <StreamVideo
                                                    activeSpeaker={remoteParticipantStreams[0].isSpeaking}
                                                    avatarUrl={getUserAvatarUrl(remoteParticipantStreams[0].peerUserId)}
                                                    fallbackInitials={getUserInitials(
                                                        remoteParticipantStreams[0].peerUserId,
                                                        remoteParticipantStreams[0].displayName
                                                    )}
                                                    label={remoteParticipantNames[0]}
                                                    sinkId={speakerDeviceId}
                                                    stream={remoteParticipantStreams[0].stream}
                                                    videoTrack={remoteParticipantStreams[0].videoTrack}
                                                />
                                                <StreamVideo
                                                    audioMuted={!localAudioIsEnabled}
                                                    avatarUrl={getUserAvatarUrl(currentMatrixUserId)}
                                                    fallbackInitials={getUserInitials(currentMatrixUserId)}
                                                    label="You"
                                                    forceCameraOff={!localVideoIsEnabled}
                                                    mirrored
                                                    muted
                                                    sinkId={speakerDeviceId}
                                                    stream={localStream}
                                                    videoEffect={liveBackgroundEffect}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <StreamVideo
                                                    audioMuted={!localAudioIsEnabled}
                                                    avatarUrl={getUserAvatarUrl(currentMatrixUserId)}
                                                    fallbackInitials={getUserInitials(currentMatrixUserId)}
                                                    label={screenShareIsActive ? "You are sharing" : "You"}
                                                    forceCameraOff={!localVideoIsEnabled}
                                                    mirrored
                                                    muted
                                                    sinkId={speakerDeviceId}
                                                    stream={localStream}
                                                    videoEffect={liveBackgroundEffect}
                                                />
                                                {remoteParticipantStreams.map((remoteStreamItem, index) => (
                                                    <StreamVideo
                                                        key={remoteStreamItem.callId || remoteStreamItem.peerUserId || index}
                                                        activeSpeaker={remoteStreamItem.isSpeaking}
                                                        avatarUrl={getUserAvatarUrl(remoteStreamItem.peerUserId)}
                                                        fallbackInitials={getUserInitials(
                                                            remoteStreamItem.peerUserId,
                                                            remoteStreamItem.displayName
                                                        )}
                                                        label={remoteParticipantNames[index]}
                                                        sinkId={speakerDeviceId}
                                                        stream={remoteStreamItem.stream}
                                                        videoTrack={remoteStreamItem.videoTrack}
                                                    />
                                                ))}
                                            </>
                                        )}
                                    </MediaGrid>
                                </CallStage>
                            </LiveCallMain>
                            {["outbound", "on_call"].includes(callState.status) && (
                                notesPanelIsCollapsed ? (
                                    <CollapsedNotesButton
                                        type="button"
                                        title="Show call notes"
                                        aria-label="Show call notes"
                                        onClick={() => setNotesPanelIsCollapsed(false)}
                                    >
                                        ‹
                                    </CollapsedNotesButton>
                                ) : (
                                    <CallNotesPanel
                                        notes={displayedCallNotes}
                                        onAddNote={onAddCallNote}
                                        onCollapse={() => setNotesPanelIsCollapsed(true)}
                                        onDeleteNote={onDeleteCallNote}
                                        onEditNote={onEditCallNote}
                                        isSending={isSendingCallNote}
                                    />
                                )
                            )}
                        </LiveCallContent>
                        <LiveCallControls
                            backgroundIsActive={liveBackgroundEffect !== "none"}
                            localAudioIsEnabled={localAudioIsEnabled}
                            localVideoIsEnabled={localVideoIsEnabled}
                            onHangup={onHangup}
                            onToggleAudio={toggleLocalAudio}
                            onToggleBackgrounds={() => {
                                setSettingsPanelIsOpen(false);
                                setLiveBackgroundPanelIsOpen(previous => !previous);
                            }}
                            onToggleScreenShare={onToggleScreenShare}
                            onToggleSettings={() => {
                                setLiveBackgroundPanelIsOpen(false);
                                setMeetingPanelIsOpen(false);
                                setSettingsPanelIsOpen(previous => !previous);
                            }}
                            onToggleMeeting={hasMeetingControls ? () => {
                                setLiveBackgroundPanelIsOpen(false);
                                setSettingsPanelIsOpen(false);
                                setMeetingPanelIsOpen(previous => !previous);
                            } : undefined}
                            onToggleVideo={toggleLocalVideo}
                            overlayPanel={activeSidePanel ? (
                                <LiveCallOverlayPanel ref={overlayPanelRef}>
                                    {activeSidePanel === "backgrounds" ? (
                                        <BackgroundEffectsPicker
                                            customBackgroundUrl={customBackgroundUrl}
                                            isApplying={liveBackgroundEffectIsApplying}
                                            onCustomBackgroundChange={setCustomBackgroundUrl}
                                            onSelect={updateLiveBackgroundEffect}
                                            processingError={liveBackgroundEffectError}
                                            selectedEffect={liveBackgroundEffect}
                                        />
                                    ) : activeSidePanel === "meeting" && hasMeetingControls ? (
                                        <MeetingManagementPanel
                                            meetingUuid={currentChat.meetingUuid}
                                            onEnded={onHangup}
                                        />
                                    ) : (
                                        <LiveSettingsPanel
                                            cameraDeviceId={cameraDeviceId}
                                            microphoneDeviceId={microphoneDeviceId}
                                            onSetCameraDevice={changeCameraDevice}
                                            onSetMicrophoneDevice={changeMicrophoneDevice}
                                            onSetSpeakerDevice={changeSpeakerDevice}
                                            speakerDeviceId={speakerDeviceId}
                                        />
                                    )}
                                </LiveCallOverlayPanel>
                            ) : null}
                            remoteScreenShareIsActive={Boolean(remoteScreenShare)}
                            screenShareIsActive={screenShareIsActive}
                        />
                    </LiveCallShell>
                </WindowBody>
            )}
        </FloatingWindow>
    );

    return (
        <>
            {callWindow}
            {waitingGuests.length > 0 && (
                <MeetingJoinRequestStack aria-live="assertive" aria-label="Guest join requests">
                    {waitingGuests.map(guest => {
                        const isUpdating = pendingAdmissionIds.has(guest.participantUuid);
                        return (
                            <MeetingJoinRequestCard key={guest.participantUuid}>
                                <MeetingJoinRequestText>
                                    {guest.displayName} wants to join.
                                </MeetingJoinRequestText>
                                <MeetingJoinRequestActions>
                                    <MeetingJoinRequestButton
                                        $tone="admit"
                                        type="button"
                                        disabled={isUpdating}
                                        onClick={() => respondToJoinRequest(guest.participantUuid, "admitted")}
                                    >
                                        Accept
                                    </MeetingJoinRequestButton>
                                    <MeetingJoinRequestButton
                                        $tone="decline"
                                        type="button"
                                        disabled={isUpdating}
                                        onClick={() => respondToJoinRequest(guest.participantUuid, "declined")}
                                    >
                                        Decline
                                    </MeetingJoinRequestButton>
                                </MeetingJoinRequestActions>
                            </MeetingJoinRequestCard>
                        );
                    })}
                </MeetingJoinRequestStack>
            )}
        </>
    );
};

export default ChatCallPanel;
